/**
 * Keeps variant option buttons honest as the customer changes their selection.
 *
 * A value gets the `option-unavailable` class (crossed out, still clickable)
 * only when nothing can be bought with it plus the options ABOVE it. So the
 * first option is crossed out only when it is sold out everywhere, and the next
 * one is crossed out when it is not available for the first one chosen.
 *
 * Clicking a value that does not exist with the other selections (Gold only
 * comes in 19.3" and 20.1", customer is on 18.1") moves the other options to a
 * variant that does exist, in stock first, so "Unavailable" is never a dead end.
 * A combination that exists but is sold out stays selected, because the customer
 * asked for that exact one and needs to see "Sold out" and the restock form.
 *
 * Listens on document rather than patching VariantSelects.prototype.onVariantChange:
 * Dawn's constructor binds that method when global.js defines the element, before
 * this file loads, so a prototype patch never runs for pickers already on the page.
 */
(function () {
  if (typeof VariantSelects === 'undefined') {
    console.warn('[variant-availability] VariantSelects class not found — skipping.');
    return;
  }

  /**
   * Compute and apply unavailability classes for every option value in this picker.
   */
  VariantSelects.prototype.updateUnavailableOptions = function () {
    if (!this.options) return;

    var variants = this.getVariantData();
    var currentOptions = this.options;
    var fieldsets = this.querySelectorAll('fieldset');

    fieldsets.forEach(function (fieldset, optionIndex) {
      var optionsAbove = currentOptions.slice(0, optionIndex);
      var inputs = fieldset.querySelectorAll('input[type="radio"]');
      inputs.forEach(function (input) {
        var buyable = variants.some(function (variant) {
          return variant.available &&
            variant.options[optionIndex] === input.value &&
            optionsAbove.every(function (opt, i) {
              return variant.options[i] === opt;
            });
        });

        var label = fieldset.querySelector('label[for="' + input.id + '"]');
        input.classList.toggle('option-unavailable', !buyable);
        if (label) label.classList.toggle('option-unavailable', !buyable);
      });
    });
  };

  /**
   * If the clicked value makes a combination that does not exist, select the
   * closest variant that has the clicked value: in stock beats sold out, then
   * whichever keeps the most of the customer's other selections.
   */
  VariantSelects.prototype.moveToExistingVariant = function (clickedInput) {
    var fieldsets = Array.from(this.querySelectorAll('fieldset'));
    var clickedIndex = fieldsets.indexOf(clickedInput.closest('fieldset'));
    if (clickedIndex === -1) return;

    var selected = fieldsets.map(function (fieldset) {
      var checked = fieldset.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });

    var variants = this.getVariantData();
    var exists = variants.some(function (variant) {
      return variant.options.every(function (opt, i) {
        return opt === selected[i];
      });
    });
    if (exists) return;

    var best = null;
    var bestScore = -1;
    variants.forEach(function (variant) {
      if (variant.options[clickedIndex] !== clickedInput.value) return;
      var score = variant.available ? 100 : 0;
      variant.options.forEach(function (opt, i) {
        if (opt === selected[i]) score += 1;
      });
      if (score > bestScore) {
        best = variant;
        bestScore = score;
      }
    });
    if (!best) return;

    fieldsets.forEach(function (fieldset, i) {
      fieldset.querySelectorAll('input[type="radio"]').forEach(function (input) {
        if (input.value === best.options[i]) input.checked = true;
      });
    });
  };

  function refresh(picker) {
    if (typeof picker.updateOptions !== 'function') return;
    picker.updateOptions();
    picker.updateUnavailableOptions();
  }

  // Capture phase: fix the selection before Dawn's handler and the theme's other
  // change listeners read it, so they all see one real variant.
  document.addEventListener('change', function (event) {
    var picker = event.target.closest('variant-radios');
    if (picker && event.target.matches('input[type="radio"]')) {
      picker.moveToExistingVariant(event.target);
    }
  }, true);

  // Bubble phase: recompute the crossed-out values after every selection change.
  document.addEventListener('change', function (event) {
    var picker = event.target.closest('variant-radios, variant-selects');
    if (picker) refresh(picker);
  });

  // Initial run on page load for any pickers already in the DOM. pageshow also
  // covers Back/Forward, where the browser can restore an earlier selection.
  function runInitial() {
    document.querySelectorAll('variant-radios, variant-selects').forEach(refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitial);
  } else {
    runInitial();
  }
  window.addEventListener('pageshow', runInitial);
})();
